/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { EntityState } from '@reduxjs/toolkit';
import type { SystemScope } from '@opengeoweb/shared';
import type { ViewPresetError } from '../viewPresets/types';
import type {
  WorkspacePresetAction,
  WorkspacePresetForBE,
  WorkspacePresetLabels,
  WorkspacePresetListItem,
  WorkspaceViewsFromBE,
} from '../workspace/types';

export enum WorkspaceListErrorCategory {
  AUTH = 'AUTH', // Errors related to authentication and authorization
  GENERIC = 'GENERIC',
}

export enum WorkspaceListErrorType {
  TRANSLATABLE_ERROR = 'TRANSLATABLE_ERROR',
  VERBATIM_ERROR = 'VERBATIM_ERROR',
}

export enum PresetFilterType {
  personal = 'personal',
  shared = 'shared',
  system = 'system',
}

export type WorkspaceListError =
  | TranslatableWorkspaceListError
  | VerbatimWorkspaceListError
  | undefined;

export interface TranslatableWorkspaceListError {
  type: WorkspaceListErrorType.TRANSLATABLE_ERROR;
  category: WorkspaceListErrorCategory;
  key: string;
  params?: Record<string, string | number> | undefined;
}

export interface VerbatimWorkspaceListError {
  type: WorkspaceListErrorType.VERBATIM_ERROR;
  category: WorkspaceListErrorCategory;
  message: string;
}

export interface WorkspaceListFilter {
  label: string;
  id: PresetFilterType;
  isSelected?: boolean;
  isDisabled?: boolean;
}

export interface FetchWorkspaceParams {
  scope?: string;
  search?: string;
}

export type WorkspaceListEntityState = EntityState<
  WorkspacePresetListItem,
  string
>;

export type WorkspaceDialogView = {
  hasChanges: boolean;
  title: string;
  isSelected: boolean;
  error?: ViewPresetError;
  scope: SystemScope;
  abstract?: string;
  description?: string;
} & WorkspaceViewsFromBE;

export interface WorkspaceFormValues {
  id?: string;
  title?: string;
  abstract?: string;
  description?: string;
  scope?: SystemScope;
  views?: WorkspaceDialogView[];
  labels?: WorkspacePresetLabels;
  updated?: string;
  updatedBy?: string;
  username?: string;
}

export type WorkspacePresetFormValues =
  | WorkspaceFormValues
  | WorkspacePresetForBE;

export interface WorkspaceActionDialogType {
  action: WorkspacePresetAction;
  presetId: string;
  formValues: WorkspacePresetFormValues;
  error?: WorkspaceListError;
  isFetching?: boolean;
  hasSaved?: boolean;
}

export interface WorkspaceListState extends WorkspaceListEntityState {
  isFetching: boolean;
  error: WorkspaceListError;
  isWorkspaceListDialogOpen: boolean;
  workspaceActionDialog?: WorkspaceActionDialogType;
  searchQuery?: string;
  workspaceListFilters?: WorkspaceListFilter[];
  workspaceLabelFilters?: WorkspacePresetLabels;
}

export type FetchWorkspaceList = FetchWorkspaceParams;

export interface FetchedWorkspaceList {
  workspaceList: WorkspacePresetListItem[];
}

export interface ErrorWorkspaceList {
  error: WorkspaceListError;
}

export interface ToggleListDialogPayload {
  isWorkspaceListDialogOpen: boolean;
}

export interface ToggleSelectFilterChip {
  id: PresetFilterType;
  isSelected: boolean;
  isAdmin?: boolean;
}

export interface SetWorkspaceLabelFiltersPayload {
  labels: WorkspacePresetLabels;
}

export interface SearchWorkspaceFilter {
  searchQuery: string;
}

export type OpenWorkspaceActionDialogPayload = WorkspaceActionDialogType;

export interface SubmitFormWorkspaceActionDialogPayload {
  presetAction: WorkspacePresetAction;
  presetId: string;
  data: WorkspacePresetForBE;
  emptyMapPresetTitle: string;
  emptyWorkspacePresetTitle: string;
}

export interface OnNonDeleteSuccesWorkspacePresetActionPayload {
  workspaceId: string;
  action: Exclude<
    WorkspacePresetAction,
    WorkspacePresetAction.DELETE | WorkspacePresetAction.DELETE_SYSTEM_PRESET
  >;
  title: string;
  abstract?: string;
  description?: string;
  currentActiveWorkspaceId?: string;
  scope: SystemScope;
  is_shared?: boolean;
  labels?: WorkspacePresetLabels;
  views?: WorkspaceViewsFromBE[];
  updated?: string;
  updatedBy?: string;
  username?: string;
}

export interface OnDeleteSuccesWorkspacePresetActionPayload {
  workspaceId: string;
  action:
    | WorkspacePresetAction.DELETE
    | WorkspacePresetAction.DELETE_SYSTEM_PRESET;
  title: string;
  abstract?: string;
  description?: string;
  currentActiveWorkspaceId?: string;
  emptyWorkspacePresetTitle: string;
  views?: WorkspaceViewsFromBE[];
}

export type OnSuccesWorkspacePresetActionPayload =
  | OnNonDeleteSuccesWorkspacePresetActionPayload
  | OnDeleteSuccesWorkspacePresetActionPayload;

export interface OnErrorWorkspacePresetActionPayload {
  error: WorkspaceListError;
}
